import {
  AdminAssessmentDto,
  AdminAssessmentListDto,
  AdminRequestDto,
  AdminRequestListDto,
  createDefaultAssessmentList,
  ASSESSMENT_STATUS
} from '../types/adminAssessment';

/**
 * Event fired (web only) whenever the stored assessment-definition list changes — bundle-content
 * unpack on cold start, a debug manual download, or an admin-result save. Consumers that cache
 * assessment definitions in memory (see ClientReportAssessments.ts) listen for this and drop
 * their cache, which is what fixes the Clients history rows rendering blank (no assessment name /
 * reports dropdown / view icons) when the list is written after those views already mounted and
 * cached an empty result. Debounced so a burst of per-assessment writes coalesces into one.
 */
export const ASSESSMENT_CONTENT_UPDATED_EVENT = 'progroups:assessment-content-updated';

let assessmentContentUpdatedTimer: ReturnType<typeof setTimeout> | null = null;
const notifyAssessmentContentUpdated = (): void => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }
  if (assessmentContentUpdatedTimer) {
    clearTimeout(assessmentContentUpdatedTimer);
  }
  assessmentContentUpdatedTimer = setTimeout(() => {
    assessmentContentUpdatedTimer = null;
    window.dispatchEvent(new CustomEvent(ASSESSMENT_CONTENT_UPDATED_EVENT));
  }, 200);
};

/**
 * AdminAssessment Storage Service - Handles assessment definitions and requests
 * This service manages the local storage of assessment definitions that sync with backend services
 */
export class AdminAssessmentStorage {
  private storage: any;
  private readonly ASSESSMENT_LIST_KEY = 'admin_assessment_list';
  private readonly REQUEST_LIST_KEY = 'admin_request_list';

  constructor(storage: any) {
    this.storage = storage;
  }

  /**
   * Initialize storage with default assessments if not present
   */
  async initialize(): Promise<void> {
    console.log('🎯 AdminAssessmentStorage: Initializing assessment storage');
    
    try {
      // Check if assessment list exists, if not create with defaults
      const existingAssessments = await this.loadAssessmentList();
      if (!existingAssessments || existingAssessments.assessments.length === 0) {
        console.log('🎯 AdminAssessmentStorage: No existing assessments found, creating defaults');
        const defaultAssessments = createDefaultAssessmentList();
        await this.saveAssessmentList(defaultAssessments);
        console.log('✅ AdminAssessmentStorage: Created default assessments:', {
          count: defaultAssessments.assessments.length,
          assessments: defaultAssessments.assessments.map(a => ({ dName: a.dName, sourceId: a.sourceId, surveybList: a.surveybList }))
        });
      } else {
        console.log('✅ AdminAssessmentStorage: Found existing assessments:', {
          count: existingAssessments.assessments.length,
          assessments: existingAssessments.assessments.map(a => ({ dName: a.dName, sourceId: a.sourceId, surveybList: a.surveybList }))
        });
      }

      // Initialize empty request list if not present
      const existingRequests = await this.loadRequestList();
      if (!existingRequests) {
        console.log('🎯 AdminAssessmentStorage: No existing requests found, creating empty list');
        await this.saveRequestList({ adminRequests: [] });
        console.log('✅ AdminAssessmentStorage: Created empty request list');
      } else {
        console.log('✅ AdminAssessmentStorage: Found existing requests:', {
          count: existingRequests.adminRequests.length
        });
      }
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to initialize:', error);
      throw error;
    }
  }

  /**
   * Load assessment list from storage
   */
  async loadAssessmentList(): Promise<AdminAssessmentListDto | null> {
    try {
      console.log('📋 AdminAssessmentStorage: Loading assessment list from storage');
      
      const data = await this.storage.getItem(this.ASSESSMENT_LIST_KEY);
      if (!data) {
        console.log('📋 AdminAssessmentStorage: No assessment data found');
        return null;
      }
      
      const parsed = JSON.parse(data) as AdminAssessmentListDto;
      
      console.log('📋 AdminAssessmentStorage: Loaded assessment list:', {
        count: parsed.assessments?.length || 0
      });
      
      return parsed;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to load assessment list:', error);
      return null;
    }
  }

  /**
   * Save assessment list to storage
   */
  async saveAssessmentList(assessmentList: AdminAssessmentListDto): Promise<void> {
    try {
      const data = JSON.stringify(assessmentList, null, 2);
      await this.storage.setItem(this.ASSESSMENT_LIST_KEY, data);

      console.log('💾 AdminAssessmentStorage: Saved assessment list:', {
        count: assessmentList.assessments.length,
        dataLength: data.length
      });

      notifyAssessmentContentUpdated();
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to save assessment list:', error);
      throw error;
    }
  }

  /**
   * Load request list from storage
   */
  async loadRequestList(): Promise<AdminRequestListDto | null> {
    try {
      const data = await this.storage.getItem(this.REQUEST_LIST_KEY);
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      console.log('📋 AdminAssessmentStorage: Loaded request list:', {
        count: parsed.adminRequests?.length || 0
      });
      
      return parsed as AdminRequestListDto;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to load request list:', error);
      return null;
    }
  }

  /**
   * Save request list to storage
   */
  async saveRequestList(requestList: AdminRequestListDto): Promise<void> {
    try {
      const data = JSON.stringify(requestList, null, 2);
      await this.storage.setItem(this.REQUEST_LIST_KEY, data);
      console.log('💾 AdminAssessmentStorage: Saved request list:', {
        count: requestList.adminRequests.length,
        dataLength: data.length
      });
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to save request list:', error);
      throw error;
    }
  }

  /**
   * Get assessment by sourceId
   */
  async getAssessmentBySourceId(sourceId: string): Promise<AdminAssessmentDto | null> {
    try {
      const assessmentList = await this.loadAssessmentList();
      if (!assessmentList) return null;
      
      const assessment = assessmentList.assessments.find(a => a.sourceId === sourceId);
      console.log('🔍 AdminAssessmentStorage: Get assessment by sourceId:', {
        sourceId,
        found: !!assessment,
        dName: assessment?.dName
      });

      // Normalize PascalCase → camelCase for report fields — unlike getAvailableAssessments,
      // this lookup was returning the raw (possibly PascalCase) stored assessment untouched,
      // which is why report.dName/reportId came back blank/undefined to callers expecting the
      // normalized camelCase shape.
      return assessment ? this.normalizeAssessment(assessment) : null;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to get assessment by sourceId:', error);
      return null;
    }
  }

  /**
   * Normalize report data from PascalCase (stored) to camelCase (interface).
   * Data may arrive from the backend or be saved in PascalCase; this ensures
   * the rest of the app always sees consistent camelCase field names.
   */
  private normalizeAssessment(assessment: any): AdminAssessmentDto {
    const normalized = { ...assessment };

    // Normalizes a single preload item's field names. Wire/C# shape is
    // {FieldCode, Type, Value, LanguageTypeId} — the same AdminPreLoadDto class used for
    // segment/account preloads elsewhere — but this was never actually mapped before (only the
    // *array's* name was normalized, e.g. reportSectionPreloads ?? ReportSectionPreloads), so
    // every preload item silently carried the raw PascalCase fields through, making
    // getPreload()/the Debug Info panel read undefined name/value for every real preload.
    const normalizePreload = (p: any) => ({
      name: p.name ?? p.FieldCode,
      value: p.value ?? p.Value,
      type: p.type ?? p.Type,
      languageTypeId: p.languageTypeId ?? p.LanguageTypeId,
    });

    if (normalized.reports && Array.isArray(normalized.reports)) {
      normalized.reports = normalized.reports.map((report: any, index: number) => {
        const subReports = (report.subReports ?? report.SubReports ?? []).map((sub: any) => ({
          dName: sub.dName ?? sub.DName,
          reportSections: (sub.reportSections ?? sub.ReportSections ?? []).map((section: any) => ({
            dName: section.dName ?? section.DName,
            reportSectionId: section.reportSectionId ?? section.ReportSectionId,
            reportScript: section.reportScript ?? section.ReportScript ?? '',
          })),
        }));

        // reportPreloads now lives on the report itself (AdminAssessmentReportDto.reportPreloads)
        // — moved up from each section's own reportSectionPreloads. Fall back to flattening any
        // old cached section-level preloads across every section of this report so content
        // downloaded before the backend's move to report-level preloads doesn't just silently
        // lose them until the next re-sync.
        const rawReportPreloads = report.reportPreloads ?? report.ReportPreloads;
        const reportPreloads = rawReportPreloads
          ? rawReportPreloads.map(normalizePreload)
          : (report.subReports ?? report.SubReports ?? []).flatMap((sub: any) =>
              (sub.reportSections ?? sub.ReportSections ?? []).flatMap((section: any) =>
                (section.reportSectionPreloads ?? section.ReportSectionPreloads ?? []).map(normalizePreload)
              )
            );

        return {
          // Fall back to a stable, position-based id (not '') when content predates the backend's
          // ReportId field — an empty string collides with the Admin Report dropdown's "None"
          // option (both render as value=""), which made a report's option indistinguishable from
          // None and snap back the instant it was picked. Most visible on single-report
          // assessments, since that's the common case, but affects any un-migrated report.
          // Uses || (not ??) because a C# string field defaults to "" on the wire, not
          // null/undefined — ?? would never reach the fallback for that common case.
          reportId: report.reportId || report.ReportId || `${assessment.sourceId ?? assessment.SourceId ?? ''}_report_${index}`,
          dName: report.dName ?? report.DName,
          targetType: report.targetType ?? report.TargetType,
          subReports,
          reportPreloads,
        };
      });
    }

    // Same defensive PascalCase→camelCase treatment as reports above — AggVars may arrive
    // un-normalized from content predating this field, or from a direct API insert that never
    // passed through contentBundler.ts's own PascalCase mapping.
    if (Array.isArray(normalized.aggVars) || Array.isArray(normalized.AggVars)) {
      normalized.aggVars = (normalized.aggVars ?? normalized.AggVars ?? []).map((v: any) => ({
        dName: v.dName ?? v.DName,
        mVar: v.mVar ?? v.MVar,
      }));
    } else {
      normalized.aggVars = [];
    }

    return normalized as AdminAssessmentDto;
  }

  /**
   * Get all available assessments
   */
  async getAvailableAssessments(): Promise<AdminAssessmentDto[]> {
    try {
      const assessmentList = await this.loadAssessmentList();
      if (!assessmentList) {
        console.warn('📋 AdminAssessmentStorage: No assessment list found - returning empty array');
        return [];
      }
      
      // Normalize PascalCase → camelCase for report fields
      const normalized = assessmentList.assessments.map(a => this.normalizeAssessment(a));

      console.log('📋 AdminAssessmentStorage: Get available assessments:', {
        count: normalized.length,
        assessmentsWithReports: normalized.filter(a => a.reports && a.reports.length > 0).length,
        assessmentNames: normalized.map(a => a.dName),
        callStack: new Error().stack?.split('\n').slice(1, 4).join(' → ')
      });
      
      // Validate each assessment has reports for debugging
      normalized.forEach((assessment, idx) => {
        if (!assessment.reports || assessment.reports.length === 0) {
          console.warn(`⚠️ Assessment ${idx} (${assessment.dName}) has no reports`);
        } else {
          console.log(`✅ Assessment ${idx} (${assessment.dName}) has ${assessment.reports.length} reports`);
        }
      });
      
      return normalized;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to get available assessments:', error);
      return [];
    }
  }

  /**
   * Add a new assessment request for a profile
   */
  async addAssessmentRequest(
    profileId: string,
    assessmentSourceId: string,
    launchqueueId?: string
  ): Promise<AdminRequestDto | null> {
    try {
      // Get the assessment definition
      const assessment = await this.getAssessmentBySourceId(assessmentSourceId);
      if (!assessment) {
        console.error('❌ AdminAssessmentStorage: Assessment not found for sourceId:', assessmentSourceId);
        return null;
      }

      // Generate launchqueue ID if not provided
      const queueId = launchqueueId || `lq_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create the request
      const request: AdminRequestDto = {
        launchqueueId: queueId,
        launchqueueData: '',
        profileId,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
        terminate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
        status: ASSESSMENT_STATUS.PENDING,
        assessment
      };

      // Add to request list
      const requestList = await this.loadRequestList() || { adminRequests: [] };
      requestList.adminRequests.push(request);
      await this.saveRequestList(requestList);

      console.log('✅ AdminAssessmentStorage: Added assessment request:', {
        profileId,
        assessmentSourceId,
        launchqueueId: queueId,
        dName: assessment.dName
      });

      return request;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to add assessment request:', error);
      return null;
    }
  }

  /**
   * Get assessment requests for a profile
   */
  async getAssessmentRequestsForProfile(profileId: string): Promise<AdminRequestDto[]> {
    try {
      const requestList = await this.loadRequestList();
      if (!requestList) return [];

      const profileRequests = requestList.adminRequests.filter(r => r.profileId === profileId);
      console.log('📋 AdminAssessmentStorage: Get requests for profile:', {
        profileId,
        count: profileRequests.length
      });

      return profileRequests;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to get requests for profile:', error);
      return [];
    }
  }

  /**
   * Update assessment request status
   */
  async updateRequestStatus(launchqueueId: string, status: string): Promise<boolean> {
    try {
      const requestList = await this.loadRequestList();
      if (!requestList) return false;

      const requestIndex = requestList.adminRequests.findIndex(r => r.launchqueueId === launchqueueId);
      if (requestIndex === -1) {
        console.warn('⚠️ AdminAssessmentStorage: Request not found:', launchqueueId);
        return false;
      }

      requestList.adminRequests[requestIndex].status = status;
      await this.saveRequestList(requestList);

      console.log('✅ AdminAssessmentStorage: Updated request status:', {
        launchqueueId,
        status
      });

      return true;
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to update request status:', error);
      return false;
    }
  }

  /**
   * Add a single assessment to the assessment list (for content downloads)
   * This will add or update an assessment in the stored assessment list
   */
  async addSingleAssessment(adminAssessmentDto: AdminAssessmentDto): Promise<void> {
    try {
      console.log('💾 AdminAssessmentStorage: Adding single assessment:', {
        dName: adminAssessmentDto.dName,
        sourceId: adminAssessmentDto.sourceId,
        languageTypeId: adminAssessmentDto.languageTypeId,
        hasReports: !!adminAssessmentDto.reports,
        reportsCount: adminAssessmentDto.reports?.length || 0
      });

      // Debug: Log the complete report structure if it exists
      if (adminAssessmentDto.reports && adminAssessmentDto.reports.length > 0) {
        console.log('📊 AdminAssessmentStorage: Assessment has reports:', {
          reports: adminAssessmentDto.reports.map((report, idx) => ({
            index: idx,
            reportId: report.reportId,
            dName: report.dName,
            targetType: report.targetType,
            hasPreloads: !!report.reportPreloads,
            preloadsCount: report.reportPreloads?.length || 0,
            hasSubReports: !!report.subReports,
            subReportsCount: report.subReports?.length || 0,
            subReports: report.subReports?.map((subReport, subIdx) => ({
              subIndex: subIdx,
              dName: subReport.dName,
              hasReportSections: !!subReport.reportSections,
              reportSectionsCount: subReport.reportSections?.length || 0,
              reportSections: subReport.reportSections?.map((section, secIdx) => ({
                sectionIndex: secIdx,
                dName: section.dName,
                reportSectionId: section.reportSectionId,
                hasReportScript: !!section.reportScript,
                reportScriptLength: section.reportScript?.length || 0
              }))
            }))
          }))
        });
      } else {
        console.warn('⚠️ AdminAssessmentStorage: Assessment has no reports - will create default structure');

        // Create default report structure for testing if missing
        adminAssessmentDto.reports = [{
          reportId: `default_${adminAssessmentDto.sourceId}`,
          dName: `Default Report for ${adminAssessmentDto.dName}`,
          targetType: 'Patient',
          reportPreloads: [],
          subReports: [{
            dName: `Assessment Results`,
            reportSections: [{
              dName: `${adminAssessmentDto.dName} Summary`,
              reportSectionId: `section_${adminAssessmentDto.sourceId}_${Date.now()}`,
              reportScript: `// Default report script for ${adminAssessmentDto.dName}\nreportOutput = "Assessment completed: " + adminResponse.recUTM + "\\n";\nreportOutput += "Assessment: ${adminAssessmentDto.dName}\\n";\nreportOutput += "Total responses: " + adminResponse.responseData.length + "\\n";`
            }]
          }]
        }];

        console.log('✅ AdminAssessmentStorage: Created default report structure');
      }

      // Load existing assessment list
      let assessmentList = await this.loadAssessmentList();
      if (!assessmentList) {
        // Create new list if none exists
        assessmentList = { assessments: [] };
      }

      // Check if assessment already exists (update if found)
      const existingIndex = assessmentList.assessments.findIndex(
        assessment => assessment.sourceId === adminAssessmentDto.sourceId
      );

      if (existingIndex >= 0) {
        // Update existing assessment
        assessmentList.assessments[existingIndex] = adminAssessmentDto;
        console.log('🔄 AdminAssessmentStorage: Updated existing assessment with reports');
      } else {
        // Add new assessment
        assessmentList.assessments.push(adminAssessmentDto);
        console.log('➕ AdminAssessmentStorage: Added new assessment with reports');
      }

      // Save the updated list
      await this.saveAssessmentList(assessmentList);
      
      console.log('✅ AdminAssessmentStorage: Single assessment added/updated successfully:', {
        totalAssessments: assessmentList.assessments.length,
        assessmentWithReports: assessmentList.assessments.filter(a => a.reports && a.reports.length > 0).length
      });
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to add single assessment:', error);
      throw error;
    }
  }

  /**
   * Re-extract AdminAssessmentDtos from ContentStore records.
   * Called as a fallback when AdminAssessmentStorage is empty but the ContentStore
   * already holds downloaded AdminAssessment content (e.g., persisted from a prior session).
   *
   * @param contentStore  Zustand ContentStore state (getContentsByType must be available)
   * @returns Number of assessments successfully extracted and saved
   */
  async repopulateFromContentStore(contentStore: any): Promise<number> {
    if (!contentStore || typeof contentStore.getContentsByType !== 'function') {
      console.warn('⚠️ AdminAssessmentStorage.repopulateFromContentStore: contentStore not available');
      return 0;
    }

    const records: any[] = contentStore.getContentsByType('AdminAssessment');
    if (records.length === 0) {
      console.log('📋 AdminAssessmentStorage.repopulateFromContentStore: no AdminAssessment records in ContentStore');
      return 0;
    }

    console.log(`📋 AdminAssessmentStorage.repopulateFromContentStore: found ${records.length} AdminAssessment record(s) in ContentStore`);

    const decodeBase64 = (b64: string): string => {
      try {
        if (typeof Buffer !== 'undefined') return Buffer.from(b64, 'base64').toString('utf-8');
        return atob(b64);
      } catch {
        throw new Error('Invalid base64 content');
      }
    };

    let extracted = 0;

    for (const record of records) {
      try {
        const tableData = record.tableData;
        const columns: string[] = tableData?.Data?.Columns ?? [];
        const rows: any[][] = tableData?.Data?.Rows ?? [];
        const bodyIdx = columns.findIndex((c: string) => c.toLowerCase() === 'content_body');

        if (bodyIdx === -1 || rows.length === 0) {
          console.warn(`⚠️ AdminAssessmentStorage.repopulateFromContentStore: record ${record.id} has no content_body`);
          continue;
        }

        for (const row of rows) {
          const base64Content = row[bodyIdx] as string;
          if (!base64Content) continue;

          const jsonString = decodeBase64(base64Content);
          const parsed = JSON.parse(jsonString);

          const dto: AdminAssessmentDto = {
            dName:          parsed.DName          ?? parsed.dName          ?? '',
            sourceId:       parsed.SourceId       ?? parsed.sourceId       ?? '',
            surveyId:       parsed.SurveyId       ?? parsed.surveyId       ?? '',
            languageTypeId: parsed.LanguageTypeId ?? parsed.languageTypeId ?? '1',
            surveybList:    parsed.SurveybList    ?? parsed.surveybList    ?? '',
            extra:          parsed.Extra          ?? parsed.extra          ?? '',
            reports:        parsed.Reports        ?? parsed.reports        ?? [],
            aggVars:        parsed.AggVars        ?? parsed.aggVars        ?? [],
          };

          await this.addSingleAssessment(dto);
          extracted++;
          console.log(`✅ AdminAssessmentStorage.repopulateFromContentStore: extracted ${dto.dName} (sourceId: ${dto.sourceId})`);
        }
      } catch (err) {
        console.error(`❌ AdminAssessmentStorage.repopulateFromContentStore: failed for record ${record.id}:`, err);
      }
    }

    console.log(`📋 AdminAssessmentStorage.repopulateFromContentStore: extracted ${extracted} assessment(s)`);
    return extracted;
  }

  /**
   * Clear all data (for testing/reset purposes)
   */
  async clearAll(): Promise<void> {
    try {
      await this.storage.removeItem(this.ASSESSMENT_LIST_KEY);
      await this.storage.removeItem(this.REQUEST_LIST_KEY);
      
      console.log('🗑️ AdminAssessmentStorage: Cleared all assessment data');
    } catch (error) {
      console.error('❌ AdminAssessmentStorage: Failed to clear data:', error);
      throw error;
    }
  }
}